import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Search, MessageSquare, Calendar, UserPlus, Sparkles, ShieldCheck,
  Camera, Inbox, TrendingUp, Heart, Eye, Lock, Star, Phone, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS: { q: string; a: string }[] = [
  // Cliente
  { q: "Come scelgo uno chef?", a: "Cerca per città, tipo di cucina o budget. Ogni profilo mostra foto, specialità, esperienza e — quando disponibili — recensioni reali. Confronta liberamente e contatta chi ti convince di più." },
  { q: "Posso parlare direttamente con lo chef?", a: "Sì. ChefConnect ti mette in contatto diretto: niente intermediari, niente call center. Concordi menù, data e dettagli direttamente con la persona che cucinerà per te." },
  { q: "Come funziona la richiesta?", a: "Apri il profilo dello chef, compila il form di richiesta (data, numero ospiti, tipo di esperienza) e invialo. Lo chef ti risponde direttamente, in media entro 24 ore." },
  { q: "Posso annullare una richiesta?", a: "Sì. Una richiesta è una proposta, non un impegno: finché non hai concordato tutto con lo chef sei libero di cambiare idea o cercare un'altra opzione." },
  { q: "Quanto costa per i clienti?", a: "Cercare e contattare uno chef è completamente gratuito. Paghi solo il servizio concordato con lui, senza commissioni nascoste." },
  // Chef
  { q: "Devo essere uno chef professionista?", a: "No. ChefConnect è aperto a personal chef, cuochi, professionisti della ristorazione e appassionati con vera esperienza. Conta la qualità del lavoro, non solo il titolo." },
  { q: "Come ottengo la verifica?", a: "Dalla dashboard puoi caricare documenti d'identità, attestato HACCP e diplomi. La Partita IVA non è obbligatoria per iniziare: il nostro team controlla i documenti e attiva il badge Verificato in 24-48h." },
  { q: "A cosa serve Premium?", a: "Premium ti dà più visibilità: priorità nei risultati, badge dedicato e presenza in evidenza in homepage e mappa. I primi 100 chef ricevono 30 giorni di Premium gratis grazie all'offerta Founder." },
  { q: "Posso usare WhatsApp?", a: "Sì. Se inserisci il tuo numero WhatsApp nel profilo, i clienti possono contattarti con un tap. È il modo più rapido per rispondere e chiudere una serata." },
  { q: "Come ricevo richieste?", a: "Ogni richiesta arriva nella tua dashboard e via email. Più il profilo è completo (foto, bio, specialità, prezzi) più aumenti le possibilità di ricevere contatti dai clienti giusti." },
  { q: "Posso iscrivermi anche senza Partita IVA?", a: "Sì. ChefConnect non richiede la Partita IVA per registrarsi. La piattaforma mette in contatto clienti e chef. Ogni professionista gestisce autonomamente gli aspetti fiscali della propria attività." },
];

export const Route = createFileRoute("/come-funziona")({
  head: () => ({
    meta: [
      { title: "Come funziona ChefConnect — Trova o diventa chef privato" },
      { name: "description", content: "Come funziona ChefConnect spiegato in modo semplice: per chi cerca uno chef e per chi cucina. Contatto diretto, profili verificati, nessun intermediario." },
      { property: "og:title", content: "Come funziona ChefConnect" },
      { property: "og:description", content: "Due percorsi semplici: trovi il tuo chef o ti fai trovare. Contatto diretto, profili verificati." },
      { property: "og:url", content: "https://chefconnectitalia.com/come-funziona" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/come-funziona" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
  component: HowItWorksPage,
});

type Step = { icon: React.ComponentType<{ className?: string }>; t: string; d: string };

const CLIENT_STEPS: Step[] = [
  { icon: Search, t: "Cerca uno chef", d: "Filtra per città, cucina e budget. Trova chi fa davvero al caso tuo." },
  { icon: Eye, t: "Sfoglia i profili", d: "Foto, bio, specialità, prezzi. Confronta con calma, senza pressioni." },
  { icon: MessageSquare, t: "Invia una richiesta", d: "Compila il form con data e ospiti. Arriva direttamente allo chef." },
  { icon: Phone, t: "Parla con lo chef", d: "Niente call center: scrivi o chiama direttamente chi cucinerà per te." },
  { icon: CheckCircle2, t: "Concorda i dettagli", d: "Menù, orari, allergie, spesa. Decidete tutto insieme, senza sorprese." },
  { icon: Heart, t: "Vivi l'esperienza", d: "Lo chef pensa a tutto: spesa, cottura, servizio, pulizia. Tu goditi la serata." },
];

const CHEF_STEPS: Step[] = [
  { icon: UserPlus, t: "Registrati", d: "Crei il tuo account in 30 secondi con email o Google. Gratis, sempre." },
  { icon: Sparkles, t: "Completa il profilo", d: "Bio, foto, specialità, prezzi, video. Più è curato, più clienti arrivano." },
  { icon: ShieldCheck, t: "Verifica la tua identità", d: "Carichi documenti e attestati. Il team controlla e attiva il badge Verificato." },
  { icon: Camera, t: "Pubblica il tuo annuncio", d: "Il profilo va online ed è subito visibile a chi cerca un chef nella tua zona." },
  { icon: Inbox, t: "Ricevi richieste", d: "Ogni contatto arriva in dashboard e via email. Rispondi tu, come preferisci." },
  { icon: TrendingUp, t: "Cresci con ChefConnect", d: "Recensioni, badge, Premium: gli strumenti per farti scegliere più spesso." },
];

const WHY: { icon: React.ComponentType<{ className?: string }>; t: string; d: string }[] = [
  { icon: ShieldCheck, t: "Chef verificati", d: "Documenti, HACCP e qualifiche controllati prima del badge Verificato. La P.IVA non è obbligatoria per iniziare." },
  { icon: Phone, t: "Contatto diretto", d: "Parli con lo chef, non con un call center. Niente intermediari." },
  { icon: Sparkles, t: "Esperienze su misura", d: "Menù pensati con te, in base a ospiti, gusti e occasione." },
  { icon: Heart, t: "Zero commissioni nascoste", d: "Paghi solo il servizio concordato con lo chef. Nient'altro." },
  { icon: Lock, t: "Privacy rispettata", d: "I tuoi dati restano tuoi. Non vendiamo contatti a terzi." },
  { icon: MessageSquare, t: "Assistenza umana", d: "Hai un dubbio? Scrivici: rispondiamo davvero, in italiano." },
];

function Timeline({ steps, accent }: { steps: Step[]; accent: "gold" | "foreground" }) {
  const dot = accent === "gold" ? "bg-gold text-gold-foreground" : "bg-foreground text-background";
  return (
    <ol className="relative space-y-6 border-l border-border pl-6 sm:pl-8">
      {steps.map((s, i) => (
        <li key={s.t} className="relative">
          <span className={`absolute -left-[34px] sm:-left-[42px] grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${dot}`}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <div className="flex items-start gap-3">
            <s.icon className="mt-1 h-5 w-5 shrink-0 text-gold" />
            <div>
              <h3 className="font-display text-lg leading-tight sm:text-xl">{s.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function HowItWorksPage() {
  return (
    <>
      {/* HERO */}
      <section className="border-b border-border bg-secondary/30">
        <div className="container mx-auto max-w-4xl px-4 py-16 text-center sm:py-24">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Come funziona</p>
          <h1 className="mt-3 font-display text-4xl leading-tight sm:text-6xl">
            Semplice da entrambe le parti.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Da una parte chi cerca uno chef per una serata speciale. Dall'altra chi cucina e vuole farsi trovare.
            ChefConnect li mette in contatto. Direttamente.
          </p>
        </div>
      </section>

      {/* DUE PERCORSI */}
      <section className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
        <div className="grid gap-16 md:grid-cols-2 md:gap-12">
          {/* Cliente */}
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Per chi cerca uno chef</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">La tua serata, in 6 passi.</h2>
            <p className="mt-3 text-sm text-muted-foreground">Dalla ricerca alla cena. Senza intermediari.</p>
            <div className="mt-8">
              <Timeline steps={CLIENT_STEPS} accent="gold" />
            </div>
            <div className="mt-10">
              <Button asChild size="lg" className="w-full sm:w-auto bg-gold text-gold-foreground hover:bg-gold/90">
                <Link to="/search">Trova uno chef</Link>
              </Button>
            </div>
          </div>

          {/* Chef */}
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Per chi cucina</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">Fatti trovare, in 6 passi.</h2>
            <p className="mt-3 text-sm text-muted-foreground">Profilo, verifica, richieste. Tutto in un posto.</p>
            <div className="mt-8">
              <Timeline steps={CHEF_STEPS} accent="foreground" />
            </div>
            <div className="mt-10">
              <Button asChild size="lg" className="w-full sm:w-auto bg-foreground text-background hover:bg-foreground/90">
                <Link to="/diventa-chef">Diventa Chef su ChefConnect</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PERCHÉ SCEGLIERCI */}
      <section className="border-y border-border bg-foreground text-background">
        <div className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Perché ChefConnect</p>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl">Sei buone ragioni, dette chiare.</h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w) => (
              <div key={w.t} className="flex flex-col gap-3 border-t border-background/20 pt-5">
                <w.icon className="h-6 w-6 text-gold" />
                <h3 className="font-display text-xl">{w.t}</h3>
                <p className="text-sm text-background/70">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIDUCIA / QUALITÀ */}
      <section className="container mx-auto max-w-4xl px-4 py-16 sm:py-24">
        <div className="rounded-2xl border border-gold/30 bg-gold-muted/20 p-8 sm:p-12">
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 text-gold" />
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">La qualità prima di tutto</p>
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl">Trasparenza, sempre.</h2>
          <ul className="mt-6 space-y-4 text-sm sm:text-base">
            <li className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span><strong>Profili verificabili.</strong> Gli chef possono caricare documenti e attestati: chi supera il controllo riceve il badge Verificato.</span>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span><strong>Tutto prima di scegliere.</strong> Foto, descrizioni, specialità e prezzi sono visibili prima di inviare qualsiasi richiesta.</span>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span><strong>Rapporto diretto.</strong> Lo chef gestisce in autonomia il dialogo con il cliente: niente filtri, niente equivoci.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-secondary/30">
        <div className="container mx-auto max-w-3xl px-4 py-16 sm:py-24">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Domande frequenti</p>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl">Tutto quello che serve sapere.</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to="/search">Trova uno chef</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/diventa-chef">Diventa Chef</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/contatti">Hai un'altra domanda?</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
